import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import {
  ButtonSizes,
  ModalSizes,
  MotifButtonModule,
  MotifFormsModule,
  MotifIconModule,
  MotifModal,
  MotifModalModule,
  MotifTooltipModule,
} from '@ey-xd/ng-motif';
import {
  IconoirXmark,
  IconoirBell,
  IconoirDoubleCheck,
} from '@ey-xd/motif-icon';
import { NotificationService } from '@services/notification.service';
import { AccessControlService } from '@services/access-control.service';
import { CommonService } from '@services/common.service';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '@shared/confirm-dialog/confirm-dialog.component';
import { GeneralMentionComponent } from './general-mention/general-mention.component';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-notification',
  imports: [
    CommonModule,
    MotifFormsModule,
    FormsModule,
    TranslatePipe,
    TranslateDirective,
    MotifTooltipModule,
    MotifButtonModule,
    MotifIconModule,
    GeneralMentionComponent,
    MotifModalModule,
  ],
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
})
export class NotificationComponent implements OnDestroy {
  closeIcon = IconoirXmark;
  bellIcon = IconoirBell;
  readIcon = IconoirDoubleCheck;
  buttonSizes = ButtonSizes;
  // Expose observables from the service and use async pipe in template
  notifications$ = this.notificationService.notifications$;
  unreadCount$ = this.notificationService.unreadCount$;
  connected$ = this.notificationService.connected$;
  totalCount$ = this.notificationService.totalCount$;
  unreadonly: boolean = false;
  isSuperAdmin: boolean = false;
  limit: number = 20;
  existingLimit: number = 20;
  emailId: string = '';

  constructor(
    private notificationService: NotificationService,
    private accessControlService: AccessControlService,
    private commonService: CommonService,
    public modal: MotifModal,
  ) {
    // Observables are consumed with async pipe in template
    this.isSuperAdmin = this.accessControlService.hasAdminAccess();
  }

  ngOnInit() {
    const userInfo = this.commonService.getUserInfo();
    this.emailId = userInfo?.Email || userInfo?.email || '';
    this.fetchNotifications();
  }

  fetchNotifications() {
    if (!this.emailId?.trim()) {
      return;
    }
    this.notificationService
      .fetchNotifications({
        fetchAll: !this.unreadonly,
        isSuperAdmin: this.isSuperAdmin,
        limit: this.limit,
        emailId: this.emailId.trim(),
      })
      .subscribe({
        next: (list) => {
          console.log('Notifications fetched', list);
        },
        error: (err) => {
          console.error('Failed to fetch notifications', err);
        },
      });
  }

  fetchUnreadNotifications() {
    this.limit = this.existingLimit;
    this.fetchNotifications();
  }

  // Prevent anchor default navigation and request more items
  loadMore(event?: MouseEvent) {
    if (event) {
      event.preventDefault();
    }
    // Increase the limit and fetch more notifications
    this.limit = this.limit + 20;
    this.fetchNotifications();
  }

  trackByNotificationId(_: number, item: any) {
    return item?.id ?? item?.NotificationId ?? _;
  }

  markAllRead() {
    this.notificationService.markAllRead().subscribe(() => {});
  }

  ngOnDestroy(): void {
    // No manual subscriptions to clean up (using async pipe)
  }

  deleteAllNotifications() {
    const message = this.commonService.getTranslationMessage(
      'ConfirmDeleteAllNotificationsMessage',
      {},
    );
    const messageTitle = this.commonService.getTranslationMessage(
      'ConfirmDeleteAllNotificationsHeader',
      {},
    );

    const modalRef = this.modal.open(ConfirmDialogComponent, {
      size: ModalSizes.XL,
      disableClose: true,
      data: {
        description: message,
        name: messageTitle,
        action: true,
        okButtonText: 'ConfirmText',
        cancelButtonText: 'Cancel',
      },
    });

    modalRef.afterClosed().subscribe((result) => {
      if (result) {
        this.notificationService.deleteAllNotifications().subscribe(() => {
          // Service will clear the notificationsSubject; template will react via async pipe
        });
      }
    });
  }
}
